import React, { useState, useCallback, useRef } from 'react';
import ConfirmDialog from '../components/ui/ConfirmDialog';

// useConfirm — promise-based in-app replacement for window.confirm.
// Built for the QA Module-2 NEW-9 sweep: native confirm()/alert() block
// the page invisibly under automation (and some real setups), so every
// remaining native dialog is being converted to the shared ConfirmDialog.
//
// Usage:
//   const { confirm, confirmElement } = useConfirm();
//   ...
//   if (!(await confirm({ title: 'Unassign job?', body: 'It will move back to the pool.', confirmLabel: 'Unassign' }))) return;
//   ...
//   return (<> ... {confirmElement} </>);
//
// `confirm(stringOrOpts)` resolves true on confirm, false on cancel /
// backdrop / ESC. Render `confirmElement` once anywhere in the component.
// Opts: { title, body, confirmLabel, cancelLabel, danger, secondaryLabel }.
// A plain string is shorthand for { body }. `danger: true` for anything
// destructive (feedback.md 2B). With `secondaryLabel`, the promise resolves
// to the string 'secondary' when that middle button is chosen.
export function useConfirm() {
    const [state, setState] = useState(null);
    const resolverRef = useRef(null);

    const confirm = useCallback((opts) => {
        const o = typeof opts === 'string' ? { body: opts } : (opts || {});
        return new Promise((resolve) => {
            // If a confirm is somehow already up, cancel it first.
            if (resolverRef.current) resolverRef.current(false);
            resolverRef.current = resolve;
            setState(o);
        });
    }, []);

    const settle = (value) => {
        setState(null);
        const r = resolverRef.current;
        resolverRef.current = null;
        if (r) r(value);
    };

    const confirmElement = (
        <ConfirmDialog
            open={!!state}
            title={state?.title || 'Are you sure?'}
            confirmLabel={state?.confirmLabel || 'Confirm'}
            cancelLabel={state?.cancelLabel || 'Cancel'}
            danger={!!state?.danger}
            secondaryLabel={state?.secondaryLabel}
            onSecondary={() => settle('secondary')}
            onConfirm={() => settle(true)}
            onCancel={() => settle(false)}>
            {state?.body || ''}
        </ConfirmDialog>
    );

    return { confirm, confirmElement };
}

export default useConfirm;
